import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ContentMenuNode } from '../utils/contentTree';
import { ArrowRight, ChevronDown, ChevronRight, Menu, MoreHorizontal, X } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import HubContainer from './HubContainer';
import { Tally } from '../components/Legend';
import { useBarOverflow } from '../hooks/useBarOverflow';
import { useGround } from '../hooks/useGround';
import { useInfoHubMenu } from '../hooks/useInfoHubMenu';
import {
  buildArticleArchivePath,
  buildHubHomePath,
  buildNewsletterArchivePath,
  buildNewsArchivePath,
  buildPagePath,
  isHubHomePath,
  MEMBER_PORTAL_PATH,
} from '../utils/contentPaths';

/** True when `pathname` is `to` itself or somewhere beneath it. */
const matchesPath = (pathname: string, to: string): boolean =>
  pathname === to || pathname.startsWith(`${to}/`);

/**
 * The info hub's top bar, built against the design system in DESIGN.md rather
 * than the app's default chrome: hairlines instead of shadows, one 2px radius,
 * two peer grounds instead of a `dark:` variant, and no apricot anywhere except
 * as a fill on the one action.
 *
 * Below `lg` the bar collapses to a burger panel. That breakpoint is the width
 * at which the logo, three or more section links and the two trailing controls
 * stop coexisting without crowding — and it is well above the widest phone, so
 * no touch device ever meets the pointer-only flyouts.
 *
 * Three things here were open items 5 and 6 in
 * docs/pages-content-frontend-handover.md §9, deferred to this pass on purpose
 * so the chrome was rebuilt once rather than patched and then redone:
 *   - the mobile/burger mode, whose absence blocked a mobile launch entirely
 *   - top-bar overflow, via `useBarOverflow` (see that file for why a scroll
 *     container was the wrong answer)
 *   - auto-flip for flyouts that would open past the right edge
 * Open item 6, the dark-mode toggle, is answered by `GroundSwitch` instead —
 * the ground *is* the axis, and shipping both controls would have been two
 * switches over one decision.
 */

/** One entry in a flyout. Bar entries and page-tree nodes share this shape. */
interface FlyoutItem {
  key: string;
  label: string;
  to: string;
  children: FlyoutItem[];
  /** Home only: it must match exactly, and it answers on three paths. */
  isHome?: boolean;
  /**
   * False for a level the path implies but no visible page occupies — 20 of
   * them in the current import. Linking one would 404, so it renders as a
   * heading over its children instead.
   */
  navigable?: boolean;
  /**
   * How many pages sit behind this entry, at any depth. Rendered as the tally in
   * the Knowledge centre legend is — same figure, same style — so a member who
   * has read one has read the other. Zero or undefined renders nothing: a count
   * of nothing is not information.
   */
  count?: number;
}

/**
 * `LegendKey`'s hover arrow: absent at rest, sliding half a step on hover.
 *
 * Always in the DOM and only faded, never conditionally rendered — the row must
 * not resize under the pointer, which is what makes a flyout list twitch as you
 * run down it. `group-hover` is safe here where the submenu's was not: this
 * lives inside the row's own anchor, which contains no nested group, so the
 * descendant selector cannot reach past the row it belongs to.
 */
const HoverArrow: React.FC = () => (
  <ArrowRight
    size={13}
    aria-hidden="true"
    className="shrink-0 text-ink-muted opacity-0 transition-all duration-fast ease-sheet group-hover:translate-x-0.5 group-hover:opacity-100"
  />
);

/**
 * The trailing icon column, always 13px wide whatever it holds.
 *
 * A chevron and an arrow answer different questions — "there is more beside this"
 * versus "this goes somewhere" — so a row shows one or the other, never both.
 * After the depth cut only sections in the "More" flyout still have children, so
 * in a bar dropdown this is the arrow on every row. The empty case is a spacer
 * rather than nothing, so a row without either still holds the column open.
 */
const RowIcon: React.FC<{ hasChildren: boolean; navigable: boolean }> = ({
  hasChildren,
  navigable,
}) => {
  if (hasChildren) {
    return <ChevronRight size={13} aria-hidden="true" className="shrink-0" />;
  }
  // A heading goes nowhere on click, so it gets no arrow promising that it does.
  return navigable ? <HoverArrow /> : <span className="w-[13px] shrink-0" aria-hidden="true" />;
};

/**
 * The menu shows a section and its direct children, and stops.
 *
 * WHY THE CUT IS HERE AND NOT IN `MenuNode`. `MenuNode` renders at two different
 * depths — one level down inside a `BarEntry` dropdown, but two inside the "More"
 * flyout, where the entry it is handed *is* a section. A cap expressed in the
 * renderer would either leave overflowed sections with no dropdown at all or let
 * the bar keep its second level; capping the data instead is measured from the
 * section in both cases, and the burger panel inherits it for free.
 *
 * What this gives up: anything below a section's direct children is no longer
 * reachable from the nav. It stays reachable by URL, by search, and from its
 * parent's own page. That is the intended trade — the legacy import runs six
 * levels deep in places (`… › 2025newpostals › 22-rimfire-pistols › 22-handgun
 * › Target`), and a bar you can walk into is a filing cabinet, not a menu.
 */
const toItem = (node: ContentMenuNode): FlyoutItem => ({
  key: node.id,
  label: node.title,
  to: buildPagePath(node.slugFull),
  // The cut. Everything below a section's direct children is dropped here — but
  // the tally still reports the whole subtree, so the depth the menu no longer
  // walks is at least visible as a number.
  children: [],
  navigable: node.navigable,
  count: node.pageCount,
});

/**
 * A section's dropdown: its direct children, and only pages.
 *
 * A grouping level with no page behind it is dropped rather than kept as a dead
 * heading — with its own children now cut it would promise a submenu that no
 * longer exists. No first-level child is group-only in the current tree, so this
 * removes nothing today; it is here so a later import cannot introduce a dead row.
 */
const toSectionChildren = (nodes: ContentMenuNode[]): FlyoutItem[] =>
  nodes.filter((node) => node.navigable).map(toItem);

/** Gap the viewport edge must keep from a flyout before it is flipped. */
const EDGE_GUTTER_PX = 8;

/* ---------------------------------------------------------------------------
   Shared type
--------------------------------------------------------------------------- */

/** Bar and flyout labels are the one label style: Poppins, the small step. */
const LABEL = 'font-ui text-step--1 whitespace-nowrap';

/**
 * The same label, allowed to wrap. The burger panel is a column of full-width
 * rows, and page titles here are admin-authored and long — the longest in the
 * live tree measures 419px against a 343px panel. With `whitespace-nowrap` the
 * panel's `overflow-y-auto` promotes overflow-x to `auto` as well, so the list
 * scrolled sideways and long titles were clipped at the edge. Wrapping is the
 * only axis that should ever move here.
 */
const PANEL_LABEL = 'font-ui text-step--1';

/* ---------------------------------------------------------------------------
   Desktop
--------------------------------------------------------------------------- */

/**
 * Decides whether a flyout would open past the right edge, and flips it if so.
 * Measured on open rather than computed from a breakpoint, because the trigger's
 * position depends on how many entries fit in the bar, which is itself dynamic.
 */
const useEdgeFlip = (
  open: boolean,
): { ref: React.RefObject<HTMLUListElement | null>; flip: boolean } => {
  const ref = useRef<HTMLUListElement>(null);
  const [flip, setFlip] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      setFlip(false);
      return;
    }
    const element = ref.current;
    if (!element) {
      return;
    }
    // Runs once per open: `flip` is reset above, so the measurement is always
    // taken in the un-flipped position and cannot oscillate.
    if (element.getBoundingClientRect().right > window.innerWidth - EDGE_GUTTER_PX) {
      setFlip(true);
    }
  }, [open]);

  return { ref, flip };
};

/**
 * A flyout panel: hairline-bounded, opaque, and never shadowed.
 *
 * NO INLINE PADDING, which is what makes the row hairlines run edge to edge
 * rather than inset like `LegendBlock`'s. That is a real difference from the
 * legend and it is forced, not chosen: a submenu is positioned `left-full` off
 * its trigger row, so padding on this panel would open a gap of exactly that
 * width between the row and its submenu. The pointer crossing that gap leaves
 * the trigger `<li>` and closes the flyout — the same reason `MenuNode` warns
 * against putting a margin there. Inset rules are not worth a menu that shuts
 * as you reach into it.
 *
 * At 13rem an edge-to-edge rule reads as the same device anyway; the legend
 * insets its rules because it is a large bounded block, not because the inset
 * is the point.
 */
const PANEL = 'absolute z-50 min-w-[13rem] rounded-sheet border border-rule-strong bg-page py-1';

/**
 * One entry inside a flyout.
 *
 * Still written recursively, but the data no longer recurses: `toItem` cuts the
 * tree at a section's direct children, so this opens a submenu only for the
 * sections listed under "More" (and for Newsroom, when it overflows there). Rows
 * in a bar dropdown are leaves and render without a chevron.
 *
 * ⚠️ Each node tracks its OWN hover state on purpose (decision C10). A CSS-only
 * version using Tailwind named groups compiles to a *descendant* selector, so
 * hovering an ancestor revealed every nested submenu at once. Don't "simplify"
 * this back to group-hover. Because each flyout is a DOM child of its trigger
 * <li>, moving the pointer into a submenu keeps its ancestors open — which is
 * also why flyouts attach with no gap: a margin would drop the hover as the
 * pointer crossed the empty space.
 */
const MenuNode: React.FC<{ item: FlyoutItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const { ref, flip } = useEdgeFlip(open);
  const hasChildren = item.children.length > 0;

  return (
    <li
      className="relative border-b border-rule last:border-b-0"
      onMouseEnter={() => hasChildren && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => hasChildren && setOpen(true)}
      onBlur={(e) => {
        // Close only when focus leaves this node's subtree entirely.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      {/* A parent is a real link too — it navigates to its own page on click.
          Unless nothing is there: a synthesised level is a heading, quieter than
          a link and without the hover affordance, so it doesn't promise a page
          it can't open.

          Both branches lay out as label / tally / icon, the last two at fixed
          widths, so the numbers and the arrows each hold a column down the panel.
          The label takes no `truncate`, unlike `LegendKey`'s: this panel is
          shrink-to-fit and sizes itself to its longest label, so a truncating
          label could shrink to nothing and collapse every row to the panel's
          min-width. Rows are `w-full` of that panel, which is what makes the two
          fixed columns beside the label line up in the first place. */}
      {item.navigable === false ? (
        // A button, not a div: it opens its submenu on focus, so the pages
        // underneath stay reachable by keyboard. It looks like a heading and
        // goes nowhere on click.
        <button
          type="button"
          aria-expanded={hasChildren ? open : undefined}
          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left uppercase tracking-[0.14em] text-ink-muted ${LABEL}`}
        >
          <span className="flex-1">{item.label}</span>
          <Tally count={item.count} unit="pages" />
          <RowIcon hasChildren={hasChildren} navigable={false} />
        </button>
      ) : (
        <NavLink
          to={item.to}
          className={({ isActive }): string =>
            `group flex w-full items-center gap-3 px-4 py-2.5 transition-colors duration-fast ease-sheet ${LABEL} ${
              isActive ? 'text-apricot' : 'text-ink-muted hover:text-apricot'
            }`
          }
        >
          <span className="flex-1">{item.label}</span>
          <Tally count={item.count} unit="pages" />
          <RowIcon hasChildren={hasChildren} navigable />
        </NavLink>
      )}

      {hasChildren && open && (
        <ul ref={ref} className={`${PANEL} top-0 ${flip ? 'right-full' : 'left-full'}`}>
          {item.children.map((child) => (
            <MenuNode key={child.key} item={child} />
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * One entry on the bar itself. Its flyout drops below it and is the last level —
 * the rows inside it are leaves, so nothing opens to the side.
 */
/** Active bar entry: apricot text with a hairline underline, the one place
 *  apricot marks selection rather than the hover-fill action affordance. */
const BAR_ACTIVE = 'text-apricot after:absolute after:inset-x-3 after:bottom-1 after:h-px after:bg-apricot';
const BAR_INACTIVE = 'text-ink-muted hover:text-apricot';

const BarEntry: React.FC<{ item: FlyoutItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const { ref, flip } = useEdgeFlip(open);
  const { pathname } = useLocation();
  const hasChildren = item.children.length > 0;

  return (
    <li
      className="relative"
      onMouseEnter={() => hasChildren && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => hasChildren && setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      {item.navigable === false ? (
        // A heading over its children, not a link — "Newsroom" has no page of
        // its own, it just groups News, Articles and Newsletters under one bar
        // entry. Lit the same as a navigable entry whenever the current page
        // is one of its children, so the grouping still reads as selected.
        <button
          type="button"
          aria-expanded={hasChildren ? open : undefined}
          className={`relative flex items-center gap-1 px-3 py-2 transition-colors duration-fast ease-sheet ${LABEL} ${
            item.children.some((child) => matchesPath(pathname, child.to)) ? BAR_ACTIVE : BAR_INACTIVE
          }`}
        >
          {item.label}
          {hasChildren && <ChevronDown size={13} aria-hidden="true" />}
        </button>
      ) : (
        <NavLink
          to={item.to}
          end={item.isHome}
          className={({ isActive }): string => {
            // Home is `/content`, which is also the prefix of every content page —
            // `isHubHomePath` lights it on the landing only.
            const active = item.isHome ? isHubHomePath(pathname) : isActive;
            return `relative flex items-center gap-1 px-3 py-2 transition-colors duration-fast ease-sheet ${LABEL} ${
              active ? BAR_ACTIVE : BAR_INACTIVE
            }`;
          }}
        >
          {item.label}
          {hasChildren && <ChevronDown size={13} aria-hidden="true" />}
        </NavLink>
      )}

      {hasChildren && open && (
        <ul ref={ref} className={`${PANEL} top-full ${flip ? 'right-0' : 'left-0'}`}>
          {item.children.map((child) => (
            <MenuNode key={child.key} item={child} />
          ))}
        </ul>
      )}
    </li>
  );
};

/** The overflow trigger: everything the bar could not fit, in one flyout. */
const OverflowEntry = React.forwardRef<HTMLLIElement, { items: FlyoutItem[] }>(
  ({ items }, forwardedRef) => {
    const [open, setOpen] = useState(false);
    const { ref, flip } = useEdgeFlip(open);

    return (
      <li
        ref={forwardedRef}
        className="relative"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setOpen(false);
          }
        }}
      >
        <button
          type="button"
          aria-label="More sections"
          aria-expanded={open}
          className={`flex items-center gap-1 px-3 py-2 text-ink-muted transition-colors duration-fast ease-sheet hover:text-apricot ${LABEL}`}
        >
          <MoreHorizontal size={15} aria-hidden="true" />
          More
        </button>

        {open && (
          // Capped and scrollable, the same guard the burger panel already had.
          // This one collects everything the bar could not fit, so its length is
          // whatever the page tree happens to be: against the live tree it opened
          // 158 entries at 5,723px in a 900px viewport, and a flyout cannot be
          // scrolled past — it closes on mouseleave — so most of it was simply
          // unreachable.
          //
          // The trade this makes: a scroll container clips, so a nested submenu
          // opened from *inside* this panel is clipped rather than escaping it.
          // That is the lesser harm and it only applies once the cap engages —
          // with a normally-sized tree the panel is shorter than the cap and
          // nothing clips. If the bar routinely overflows this far, the fix
          // belongs in the tree, not here.
          <ul
            ref={ref}
            className={`${PANEL} top-full max-h-[70vh] overflow-y-auto ${flip ? 'right-0' : 'left-0'}`}
          >
            {items.map((item) => (
              <MenuNode key={item.key} item={item} />
            ))}
          </ul>
        )}
      </li>
    );
  },
);
OverflowEntry.displayName = 'OverflowEntry';

/* ---------------------------------------------------------------------------
   Mobile
--------------------------------------------------------------------------- */

/**
 * One entry in the burger panel. A parent is a link *and* a disclosure: the link
 * navigates, the separate chevron button expands. Collapsing the two into one
 * control would make a parent page unreachable on touch, which is the trap the
 * hover-only desktop menu already avoids by letting a parent navigate on click.
 */
const MobileNode: React.FC<{ item: FlyoutItem; depth: number }> = ({ item, depth }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = item.children.length > 0;

  return (
    <li>
      <div className="flex items-stretch border-b border-rule">
        {item.navigable === false ? (
          // No page behind this level — a heading, not a dead link. The chevron
          // beside it is the only control, which is what expands it.
          <span
            className={`flex-1 py-3 uppercase tracking-[0.14em] text-ink-muted ${PANEL_LABEL}`}
            style={{ paddingInlineStart: `${depth * 1.1}rem` }}
          >
            {item.label}
          </span>
        ) : (
          <NavLink
            to={item.to}
            end={item.isHome}
            className={({ isActive }): string =>
              `flex-1 py-3 transition-colors duration-fast ease-sheet ${PANEL_LABEL} ${
                isActive ? 'text-apricot' : 'text-ink-muted'
              }`
            }
            style={{ paddingInlineStart: `${depth * 1.1}rem` }}
          >
            {item.label}
          </NavLink>
        )}

        {/* Self-centred rather than stretched: the label beside it can wrap to two
            lines in this panel, and a stretched tally would ride the first one.
            Always present, like everywhere else, so the numbers hold a column
            down the panel. */}
        <span className="flex items-center py-3 pl-3">
          <Tally count={item.count} unit="pages" />
        </span>

        {/* The disclosure column, held open at the chevron button's own width even
            when there is nothing to disclose — otherwise a childless row lets its
            tally drift right, out of line with the rows above it. */}
        {!hasChildren && <span className="w-[2.5rem] shrink-0" aria-hidden="true" />}

        {hasChildren && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label}`}
            className="px-3 text-ink-muted transition-colors duration-fast ease-sheet hover:text-ink"
          >
            <ChevronDown
              size={16}
              aria-hidden="true"
              className={`transition-transform duration-fast ease-sheet ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {hasChildren && expanded && (
        <ul>
          {item.children.map((child) => (
            <MobileNode key={child.key} item={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

/* ---------------------------------------------------------------------------
   The bar
--------------------------------------------------------------------------- */

const InfoHubMenu: React.FC = () => {
  const { sections } = useInfoHubMenu();
  const { ground } = useGround();
  const { pathname } = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);

  /**
   * Bar order: Home, then the content sections, then Newsroom last.
   *
   * The whole bar is now a fixed length — three entries plus however many
   * sections `hubSections.ts` declares, nine today. It used to be four plus the
   * page tree's top level, which the legacy import made 158 entries long. The
   * dropdowns underneath are still entirely data-driven; it is only the top row
   * that is decided in code.
   *
   * News, Articles and Newsletters are peers with no page of their own to link
   * from the bar, so they group under one "Newsroom" entry rather than three
   * separate slots — one flyout instead of three bar positions. Newsroom is
   * placed last on purpose, so it is also the first thing overflow eats from a
   * narrow viewport — the content sections stay visible ahead of it.
   */
  const entries: FlyoutItem[] = useMemo(
    () => [
      { key: 'home', label: 'Home', to: buildHubHomePath(), children: [], isHome: true },
      ...sections.map((section) => ({
        key: section.key,
        label: section.label,
        to: buildPagePath(section.to),
        children: toSectionChildren(section.children),
        // Only ever drawn when the section is pushed into "More" or the burger
        // panel — `BarEntry` renders the label alone, because a row of numbers
        // across the top bar would be chrome competing with the sections.
        count: section.pageCount,
      })),
      {
        key: 'newsroom',
        label: 'Newsroom',
        to: '',
        navigable: false,
        children: [
          { key: 'news', label: 'News', to: buildNewsArchivePath(), children: [] },
          { key: 'articles', label: 'Articles', to: buildArticleArchivePath(), children: [] },
          { key: 'newsletters', label: 'Newsletters', to: buildNewsletterArchivePath(), children: [] },
        ],
      },
    ],
    [sections],
  );

  const { containerRef, measureRef, moreRef, visible, overflow } = useBarOverflow(entries);

  // A navigation with the panel open should close it — otherwise the member taps
  // a link and is left looking at the menu they just used.
  useEffect(() => setPanelOpen(false), [pathname]);

  useEffect(() => {
    if (!panelOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setPanelOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [panelOpen]);

  // The linear lockup, one file per ground, both drawn at the same aspect
  // ratio so one className covers them both. Picked in JS because `dark:` no
  // longer applies — the ground is an attribute on <html>, not a class, and
  // this is the one place a component must know which is live.
  const logo = {
    src: ground === 'navy' ? '/images/logo-linear-white.png' : '/images/logo-linear-navy.png',
    className: 'h-9 w-auto',
  };

  return (
    <header className="shrink-0 border-b border-rule bg-page">
      <HubContainer className="flex h-16 items-center gap-4">
        {/* The logo is the way back to the member portal, not to the hub's own
            landing — "Home" on the bar already covers that. There is exactly
            one door back into the app, and it should not compete with a
            second control for the same job. */}
        <NavLink to={MEMBER_PORTAL_PATH} className="flex shrink-0 items-center">
          <img src={logo.src} alt="Natshoot" className={logo.className} />
        </NavLink>

        {/* Desktop bar. `min-w-0` is load-bearing: without it the flex item
            refuses to shrink below its content and the overflow measurement
            never sees a constrained budget. `justify-end` on the list is what
            groups the entries against the trailing controls rather than
            against the logo, leaving the run of empty bar between them — the
            list's own width (the overflow budget) is unaffected, since that is
            read from `clientWidth`, not from where its children sit inside it. */}
        <nav className="hidden min-w-0 flex-1 lg:block" aria-label="Info hub">
          <ul ref={containerRef} className="flex items-center justify-end gap-1">
            {visible.map((item) => (
              <BarEntry key={item.key} item={item} />
            ))}
            {overflow.length > 0 && <OverflowEntry ref={moreRef} items={overflow} />}
          </ul>

          {/* The hidden full list the widths are measured from. `aria-hidden`
              and `inert`-by-tabIndex so it is never reachable or announced.

              The 0×0 clipping wrapper is load-bearing, not decoration. The list
              lays out at its full natural width — 34,720px against the live page
              tree — and an absolutely positioned element still contributes that
              width to its containing block's scrollable overflow. With the list
              positioned directly against the initial containing block it pushed
              `documentElement.scrollWidth` to 35,076px; `overflow-x: hidden` on
              html/body hid the scrollbar but the document stayed programmatically
              scrollable sideways. An ancestor with `overflow: hidden` stops that
              propagation, and clipping is purely visual — `getBoundingClientRect`
              still reports each entry's true width, so the measurement is
              unaffected. */}
          <div
            aria-hidden="true"
            className="pointer-events-none invisible absolute h-0 w-0 overflow-hidden"
          >
            <ul ref={measureRef} className="flex w-max items-center gap-1">
              {entries.map((item) => (
                <li
                  key={item.key}
                  className={`flex items-center gap-1 px-3 py-2 ${LABEL}`}
                  tabIndex={-1}
                >
                  {item.label}
                  {item.children.length > 0 && <ChevronDown size={13} aria-hidden="true" />}
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Trailing controls. The ground switch used to sit here; it now floats
            in the bottom-right corner (see `InfoHubLayout`), handing ~154px back
            to the bar. Measured at 1440: that did *not* buy another section —
            the third one came from the 82rem shell, not from this — so treat the
            space as headroom for longer labels and narrower viewports rather
            than as a win already banked.
            Member Portal used to live here as its own button; the logo now
            carries that job (see the link above), so the burger trigger is the
            only thing left in this slot. */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setPanelOpen((value) => !value)}
            aria-expanded={panelOpen}
            aria-controls="hub-menu-panel"
            aria-label={panelOpen ? 'Close menu' : 'Open menu'}
            className="rounded-sheet border border-rule-strong p-2 text-ink transition-colors duration-fast ease-sheet hover:border-apricot lg:hidden"
          >
            {panelOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </HubContainer>

      {/* Burger panel. Capped and scrollable so a deep tree cannot run past the
          bottom of the viewport with no way back to the bar. */}
      {panelOpen && (
        <nav
          id="hub-menu-panel"
          aria-label="Info hub"
          className="border-t border-rule bg-page lg:hidden"
        >
          <HubContainer>
            <ul className="max-h-[70vh] overflow-y-auto py-2">
              {entries.map((item) => (
                <MobileNode key={item.key} item={item} depth={0} />
              ))}
            </ul>
          </HubContainer>
        </nav>
      )}
    </header>
  );
};

export default InfoHubMenu;
